import {
    useQueryClient,
    useMutation,
    useQuery,
    QueryClient,
} from '@tanstack/react-query';
import {
    AgentArrivalHypothesis,
    DetailedAgentArrivalHypothesis,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/types';
import { resetResultSimulationDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/hooks/resultSimulationData';
import {
    END_DATE,
    START_DATE,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';

import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import {
    AGENT_FIRST_NAME,
    AGENT_LAST_NAME,
    ETP,
    IFSE,
    JOB_LABEL,
    JOB_NUMBER,
    MANTY_STATUS,
    ORG_CHART_HR_SOFTWARE_ID,
    RECURRENCE,
    RETIREMENT_SYSTEM,
    SALARY,
} from 'routes/HumanResourcesModule/business/definitions';
import { RQAPICallWithData } from 'store/APIClient';
import { dispatch } from 'store/store';
import { useHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/contexts/HypothesesChangesContext';
import {
    getSimulationAgentDataFromCache,
    setSimulationAgentDataFromCache,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/hooks/agentsSimulation';
import {
    MantyStatus,
    RecurrenceEnum,
    RetirementSystem,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import { OFFICIAL_GRADE } from 'routes/HumanResourcesModule/Settings/HrGrade/helpers/constants';
import {
    ECHELON,
    FILIERE,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/helpers/constants';

const AGENT_ARRIVAL_HYPOTHESES_API_KEY = 'agent_arrival_hypotheses';
const DETAILED_AGENT_ARRIVAL_HYPOTHESES_API_KEY =
    'detailed_agent_arrival_hypotheses';

export type AgentArrivalHypothesisMutationParams = Pick<
    AgentArrivalHypothesis,
    | typeof AGENT_FIRST_NAME
    | typeof AGENT_LAST_NAME
    | typeof ECHELON
    | typeof END_DATE
    | typeof ETP
    | typeof FILIERE
    | typeof IFSE
    | typeof JOB_LABEL
    | typeof JOB_NUMBER
    | typeof MANTY_STATUS
    | typeof OFFICIAL_GRADE
    | typeof ORG_CHART_HR_SOFTWARE_ID
    | typeof RECURRENCE
    | typeof RETIREMENT_SYSTEM
    | typeof SALARY
    | typeof START_DATE
>;

const invalidateDetailedAgentArrivalHypothesesQueries = (
    hrSimulationId: number,
    queryClient: QueryClient,
) => {
    queryClient.invalidateQueries({
        queryKey: [DETAILED_AGENT_ARRIVAL_HYPOTHESES_API_KEY, hrSimulationId],
    });
};

export const useGetAgentArrivalHypotheses = (hrSimulationId: number) =>
    useQuery({
        queryKey: [AGENT_ARRIVAL_HYPOTHESES_API_KEY, hrSimulationId],

        queryFn: async () => {
            const response = await RQAPICallWithData<AgentArrivalHypothesis[]>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/agent_arrival_hypotheses`,
            });
            return response.content.data;
        },
    });

const useCreateAgentArrivalHypothesisMutation = (
    hrSimulationId: number,
    onAgentArrivalHypothesisCreated: () => void,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: AgentArrivalHypothesisMutationParams) =>
            RQAPICallWithData<AgentArrivalHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/agent_arrival_hypotheses`,
                method: 'POST',
                body: payload,
            }),

        onSuccess: (response) => {
            onAgentArrivalHypothesisCreated();
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                [AGENT_ARRIVAL_HYPOTHESES_API_KEY, hrSimulationId],
                (oldData: AgentArrivalHypothesis[]) => [
                    ...oldData,
                    response.content.data,
                ],
            );

            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.simulation.recurrent
                    .createAgentArrivalHypothesis,
                {
                    etp: response.content.data.etp,
                    salary: response.content.data.salary,
                    mantystatus: response.content.data.mantyStatus,
                },
            );
        },
        meta: { useGlobalOnError: true },
    });
};

/**
 * Removes an agent's data from the simulation cache based on the given software ID.
 *
 * @param {number} hrSimulationId - The ID of the HR simulation.
 * @param {string} agentSoftwareId - The software ID of the agent to remove.
 * @param {QueryClient} queryClient - The QueryClient instance from react-query.
 */
function removeAgentFromSimulationDataCache(
    queryClient: QueryClient,
    hrSimulationId: number,
    agentSoftwareId: string,
): void {
    const currentData = getSimulationAgentDataFromCache(
        queryClient,
        hrSimulationId,
        RecurrenceEnum.RECURRENT,
    );

    if (currentData?.data) {
        const updatedData = {
            ...currentData,
            data: currentData.data.filter(
                (agentData) => agentData.agentSoftwareId !== agentSoftwareId,
            ),
        };
        setSimulationAgentDataFromCache(
            queryClient,
            hrSimulationId,
            RecurrenceEnum.RECURRENT,
            updatedData,
        );
    }
}

const useDeleteAgentArrivalHypothesisMutation = (
    hrSimulationId: number,
    onAgentArrivalHypothesisDeleted: () => void,
    hypothesisId?: number,
    agentSoftwareId?: string,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            RQAPICallWithData({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/agent_arrival_hypothesis/${hypothesisId}`,
                method: 'DELETE',
            }),

        onSuccess: (response) => {
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            onAgentArrivalHypothesisDeleted();
            queryClient.setQueryData(
                [AGENT_ARRIVAL_HYPOTHESES_API_KEY, hrSimulationId],
                (oldData: AgentArrivalHypothesis[]) =>
                    oldData.filter(
                        (hypothesis) => hypothesis.id !== hypothesisId,
                    ),
            );

            if (agentSoftwareId) {
                removeAgentFromSimulationDataCache(
                    queryClient,
                    hrSimulationId,
                    agentSoftwareId,
                );
            }
        },
        meta: { useGlobalOnError: true },
    });
};

/**
 * Updates informational data of an agent within the simulation cache. This function
 * alters agent's non-financial details like last name and job label without affecting
 * their financial data in the simulation.
 *
 * @param {QueryClient} queryClient - The QueryClient instance to interact with the react-query cache.
 * @param {number} hrSimulationId - The identifier for the HR simulation.
 * @param {string} agentSoftwareId - The software identifier for the agent whose data is to be updated.
 * @param {string} agentFirstName - The new last name to set for the agent.
 * @param {string} agentLastName - The new first name to set for the agent.
 * @param {string} jobLabel - The new job label to set for the agent.
 *  @param {string} officialGrade - The new grade to set for the agent.
 * @param {number} echelon - The new echelon to set for the agent.
 * @param {number} ifse - The ifse to set for the agent.
 * @param {MantyStatus} mantyStatus - The manty status to set for the agent.
 * @param {RetirementSystem} retirementSystem - The retirement system to set for the agent.
 */
function updateAgentInSimulationDataCache(
    queryClient: QueryClient,
    hrSimulationId: number,
    agentSoftwareId: string,
    agentFirstName: string,
    agentLastName: string,
    jobLabel: string,
    jobNumber: string,
    mantyStatus: MantyStatus,
    retirementSystem: RetirementSystem,
    officialGrade: string,
    echelon: number,
    ifse: number,
    filiere: string,
): void {
    const currentData = getSimulationAgentDataFromCache(
        queryClient,
        hrSimulationId,
        RecurrenceEnum.RECURRENT,
    );

    if (currentData?.data) {
        const updatedData = {
            ...currentData,
            data: currentData.data.map((agentData) =>
                agentData.agentSoftwareId === agentSoftwareId
                    ? {
                          ...agentData,
                          jobLabel,
                          jobNumber,
                          agentFirstName,
                          agentLastName,
                          mantyStatus,
                          retirementSystem,
                          officialGrade,
                          echelon,
                          ifse,
                          filiere,
                      }
                    : agentData,
            ),
        };

        setSimulationAgentDataFromCache(
            queryClient,
            hrSimulationId,
            RecurrenceEnum.RECURRENT,
            updatedData,
        );
    }
}

const useUpdateAgentArrivalHypothesisMutation = (
    hrSimulationId: number,
    onAgentArrivalHypothesisUpdated: () => void,
    hypothesisId?: number,
    agentSoftwareId?: string,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            agentFirstName,
            agentLastName,
            echelon,
            endDate,
            etp,
            filiere,
            ifse,
            jobLabel,
            jobNumber,
            mantyStatus,
            officialGrade,
            orgChartHrSoftwareId,
            recurrence,
            retirementSystem,
            salary,
            startDate,
        }: AgentArrivalHypothesisMutationParams) => {
            if (hypothesisId === undefined) return undefined;

            return RQAPICallWithData<AgentArrivalHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/agent_arrival_hypothesis/${hypothesisId}`,
                method: 'PUT',
                body: {
                    agentFirstName,
                    agentLastName,
                    echelon,
                    endDate,
                    etp,
                    filiere,
                    ifse,
                    jobLabel,
                    jobNumber,
                    mantyStatus,
                    officialGrade,
                    orgChartHrSoftwareId,
                    recurrence,
                    retirementSystem,
                    salary,
                    startDate,
                },
            });
        },

        onSuccess: (response) => {
            dispatch.vision.notifyUser({
                message: response?.content.message || '',
                snackType: 'success',
            });
            onAgentArrivalHypothesisUpdated();
            const updatedAgentData = response?.content.data;
            queryClient.setQueryData(
                [AGENT_ARRIVAL_HYPOTHESES_API_KEY, hrSimulationId],
                (oldData: AgentArrivalHypothesis[]) =>
                    oldData.map((hypothesis) =>
                        hypothesis.id === hypothesisId
                            ? updatedAgentData
                            : hypothesis,
                    ),
            );

            if (agentSoftwareId && updatedAgentData) {
                updateAgentInSimulationDataCache(
                    queryClient,
                    hrSimulationId,
                    agentSoftwareId,
                    updatedAgentData.agentFirstName,
                    updatedAgentData.agentLastName,
                    updatedAgentData.jobLabel,
                    updatedAgentData.jobNumber,
                    updatedAgentData.mantyStatus,
                    updatedAgentData.retirementSystem,
                    updatedAgentData.officialGrade,
                    updatedAgentData.echelon,
                    updatedAgentData.ifse,
                    updatedAgentData.filiere,
                );
            }
        },
        meta: { useGlobalOnError: true },
    });
};

export const useGetDetailedAgentArrivalHypotheses = (hrSimulationId: number) =>
    useQuery({
        queryKey: [DETAILED_AGENT_ARRIVAL_HYPOTHESES_API_KEY, hrSimulationId],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                DetailedAgentArrivalHypothesis[]
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/detailed_agent_arrival_hypotheses`,
            });
            return response.content.data;
        },
    });
export const useAgentArrivalHypothesisCRUDMutations = ({
    simulationId,
    hypothesisId,
    agentSoftwareId,
}: {
    simulationId: number;
    hypothesisId?: number;
    agentSoftwareId?: string;
}) => {
    const { setHasChanges } = useHypothesesChangesContext();
    const queryClient = useQueryClient();
    const onSuccess = () => {
        invalidateDetailedAgentArrivalHypothesesQueries(
            simulationId,
            queryClient,
        );
        // as we have hypotheses changes, we reset the result queries
        resetResultSimulationDataQueries(queryClient, simulationId);
        setHasChanges(true);
    };

    const { mutate: createHypothesis, isPending: createLoading } =
        useCreateAgentArrivalHypothesisMutation(simulationId, onSuccess);
    const { mutate: editHypothesis, isPending: editLoading } =
        useUpdateAgentArrivalHypothesisMutation(
            simulationId,
            onSuccess,
            hypothesisId,
            agentSoftwareId,
        );
    const { mutate: deleteHypothesis, isPending: deleteLoading } =
        useDeleteAgentArrivalHypothesisMutation(
            simulationId,
            onSuccess,
            hypothesisId,
            agentSoftwareId,
        );

    return {
        createHypothesis,
        editHypothesis,
        deleteHypothesis,
        isHypothesisMutationLoading:
            createLoading || editLoading || deleteLoading,
    };
};
